import type { Vec2 } from "../types";
import { pointInPolygon } from "../geom";
import { rect, rotate, translate } from "./geometry";

/**
 * A sink slides along one path: the counter's centerline. Straight runs are
 * segments; each inside corner is a quarter-circle fillet so the basin turns
 * with the slab instead of spinning in place. The wall (and the faucet) stays
 * on the left of travel.
 *
 * `sinkAt` is a permille along that path. `-1` means the fixture has not been
 * dragged and sits centered on the run you stand at ({@link SinkTrack.autoPermille}).
 */

export const SINK_AT_AUTO = -1;
export const SINK_AT_MAX = 1000;

export interface SinkFixture {
  n: number;
  basinW: number;
  basinD: number;
  gap: number;
  faucetW: number;
  faucetH: number;
  cornerRadius: number;
}

export interface SinkPose {
  x: number;
  y: number;
  /** Degrees clockwise. 0 = basins along +x, faucet toward −y (the wall). */
  angle: number;
  permille: number;
}

interface LineSeg {
  kind: "line";
  a: Vec2;
  b: Vec2;
  heading: number;
  length: number;
}

interface ArcSeg {
  kind: "arc";
  center: Vec2;
  radius: number;
  h0: number;
  h1: number;
  length: number;
}

type Seg = LineSeg | ArcSeg;

interface Compiled {
  segs: Seg[];
  length: number;
  /** Arc length at the middle of each corner fillet. */
  cornerAt: number[];
}

export interface SinkTrack {
  length: number;
  /** Rounded-rect radius for each basin, inches. */
  cornerRadius: number;
  /** Permille of the centered, pre-drag pose. */
  autoPermille: number;
  /** Permille of each corner arc's midpoint (empty on a straight run). */
  cornerPermilles: number[];
  poseAt(permille: number): SinkPose;
  /** Nearest point on the path, as a permille. */
  project(point: Vec2): number;
  /** Last permille between `from` and `to` whose basins and faucet stay on the slab. */
  clampPermille(from: number, to: number): number;
  /** Closest permille to `target` that still fits, or the automatic pose. */
  nearestValid(target: number): number;
  fits(permille: number): boolean;
  /** Basin then faucet outline for each bowl, in footprint-local inches. */
  outlines(pose: SinkPose): { basin: Vec2[]; faucet: Vec2[] }[];
}

/** Basin count and size for a rectangular host. `null` when nothing fits. */
export function sizeSinkFixture(host: { w: number; d: number }, kind: string): SinkFixture | null {
  let n = kind === "double" ? 2 : kind === "single" ? 1 : 0;
  if (n === 2 && host.w < 28) n = 1;
  if (n === 0 || host.w < 12 || host.d < 8) return null;
  const gap = 1.4;
  const basinW = Math.min(n === 2 ? 15 : 20, (host.w - gap * (n - 1)) / n);
  const basinD = Math.min(16, host.d * 0.62);
  if (basinW < 6 || basinD < 5) return null;
  return {
    n,
    basinW,
    basinD,
    gap,
    faucetW: Math.min(1.8, basinW * 0.16),
    faucetH: Math.min(1.3, basinD * 0.14),
    cornerRadius: Math.min(2.5, basinW * 0.18, basinD * 0.22),
  };
}

/**
 * Distance from the wall side of a span to the fixture center.
 * Matches the historical "slightly toward the user" basin placement.
 */
export function fixtureCenterOffset(span: number, basinD: number): number {
  return (span - basinD) * 0.55 + basinD / 2;
}

interface BuildInput {
  /** Sharp centerline. Travel keeps the wall on the left, so corners are right turns. */
  points: Vec2[];
  /** Center of the fixture before anyone drags it. Must lie on `points`. */
  auto: Vec2;
  fixture: SinkFixture;
  footprint: Vec2[];
}

export function buildSinkTrack(input: BuildInput): SinkTrack | null {
  const { points, auto, fixture, footprint } = input;
  if (points.length < 2) return null;
  if (points.some((p) => !Number.isFinite(p.x) || !Number.isFinite(p.y))) return null;

  const radius = chooseRadius(points, footprint, fixture);
  const compiled = compile(points, radius);
  if (!compiled || compiled.length <= 0) return null;
  const path = compiled;

  const poseAt = (permille: number) => poseOn(path, permille);
  const project = (point: Vec2) => projectOn(path, point);
  const outlines = (pose: SinkPose) => fixtureOutlines(fixture, pose);

  function fits(permille: number): boolean {
    return poseFits(path, fixture, footprint, permille);
  }

  function nearestValid(target: number): number {
    const goal = clampNum(Math.round(target), 0, SINK_AT_MAX);
    if (fits(goal)) return goal;
    const step =
      path.length < 1e-6 ? SINK_AT_MAX : Math.max(1, Math.round((0.5 / path.length) * SINK_AT_MAX));
    for (let d = step; d <= SINK_AT_MAX; d += step) {
      const lo = goal - d;
      const hi = goal + d;
      if (lo >= 0 && fits(lo)) return lo;
      if (hi <= SINK_AT_MAX && fits(hi)) return hi;
    }
    const fallback = project(auto);
    return fits(fallback) ? fallback : goal;
  }

  function clampPermille(from: number, to: number): number {
    const a = clampNum(from, 0, SINK_AT_MAX);
    const b = clampNum(to, 0, SINK_AT_MAX);
    const goal = Math.round(b);
    if (fits(goal)) return goal;
    const start = fits(Math.round(a)) ? Math.round(a) : nearestValid(a);
    if (start === goal) return start;
    const inches = (Math.abs(goal - start) / SINK_AT_MAX) * path.length;
    const steps = Math.max(1, Math.ceil(inches / 0.5));
    let last = start;
    for (let i = 1; i <= steps; i++) {
      const t = Math.round(start + ((goal - start) * i) / steps);
      if (t === last) continue;
      if (!fits(t)) return last;
      last = t;
    }
    return last;
  }

  const autoPermille = project(auto);
  const cornerPermilles = path.cornerAt.map((s) =>
    clampNum(Math.round((s / path.length) * SINK_AT_MAX), 0, SINK_AT_MAX)
  );

  return {
    length: path.length,
    cornerRadius: fixture.cornerRadius,
    autoPermille,
    cornerPermilles,
    poseAt,
    project,
    clampPermille,
    nearestValid,
    fits,
    outlines,
  };
}

/** True when `point` lies in a sink basin or faucet. */
export function pointHitsSink(parts: { role: string; outline: Vec2[] }[], point: Vec2): boolean {
  return parts.some((p) => p.role === "detail" && pointInPolygon(point, p.outline));
}

/** Room point → footprint-local inches, undoing the piece's clockwise rotation. */
export function roomToPieceLocal(room: Vec2, origin: Vec2, rotationDeg: number): Vec2 {
  const dx = room.x - origin.x;
  const dy = room.y - origin.y;
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return { x: dx * cos + dy * sin, y: -dx * sin + dy * cos };
}

/**
 * Largest fillet a single basin can sit on without leaving the slab.
 * Doubles share that path and stop where the pair no longer fits.
 */
function chooseRadius(points: Vec2[], footprint: Vec2[], fixture: SinkFixture): number {
  const probe: SinkFixture = { ...fixture, n: 1, gap: 0, basinW: Math.min(20, fixture.n === 1 ? fixture.basinW : 20) };
  const max = maxFillet(points);
  for (let r = max; r >= 0.5; r -= 0.5) {
    const compiled = compile(points, r);
    if (!compiled || compiled.cornerAt.length === 0) continue;
    const ok = compiled.cornerAt.every((s) => {
      const permille = (s / compiled.length) * SINK_AT_MAX;
      return poseFits(compiled, probe, footprint, permille);
    });
    if (ok) return r;
  }
  return 0;
}

function poseFits(compiled: Compiled, fixture: SinkFixture, footprint: Vec2[], permille: number): boolean {
  const pose = poseOn(compiled, permille);
  if (!pointInPolygon({ x: pose.x, y: pose.y }, footprint)) return false;
  for (const part of fixtureOutlines(fixture, pose)) {
    if (!polygonInside(footprint, part.basin)) return false;
    if (!polygonInside(footprint, part.faucet)) return false;
  }
  return true;
}

function poseOn(compiled: Compiled, permille: number): SinkPose {
  const t = clampNum(permille, 0, SINK_AT_MAX);
  const s = (t / SINK_AT_MAX) * compiled.length;
  let acc = 0;
  for (let i = 0; i < compiled.segs.length; i++) {
    const seg = compiled.segs[i];
    const last = i === compiled.segs.length - 1;
    if (!last && acc + seg.length < s - 1e-6) {
      acc += seg.length;
      continue;
    }
    const u = seg.length < 1e-9 ? 0 : clampNum((s - acc) / seg.length, 0, 1);
    if (seg.kind === "line") {
      return {
        x: seg.a.x + (seg.b.x - seg.a.x) * u,
        y: seg.a.y + (seg.b.y - seg.a.y) * u,
        angle: seg.heading,
        permille: t,
      };
    }
    const h = seg.h0 + (seg.h1 - seg.h0) * u;
    const p = arcPoint(seg.center, seg.radius, h);
    return { x: p.x, y: p.y, angle: h, permille: t };
  }
  const end = compiled.segs[compiled.segs.length - 1];
  const p = end.kind === "line" ? end.b : arcPoint(end.center, end.radius, end.h1);
  const angle = end.kind === "line" ? end.heading : end.h1;
  return { x: p.x, y: p.y, angle, permille: t };
}

function projectOn(compiled: Compiled, point: Vec2): number {
  let best = Infinity;
  let bestS = 0;
  let acc = 0;
  for (const seg of compiled.segs) {
    const hit = seg.kind === "line" ? projectLine(seg, point) : projectArc(seg, point);
    if (hit.dist < best) {
      best = hit.dist;
      bestS = acc + hit.t * seg.length;
    }
    acc += seg.length;
  }
  if (compiled.length < 1e-6) return 0;
  return clampNum(Math.round((bestS / compiled.length) * SINK_AT_MAX), 0, SINK_AT_MAX);
}

function fixtureOutlines(fixture: SinkFixture, pose: SinkPose): { basin: Vec2[]; faucet: Vec2[] }[] {
  const totalW = fixture.n * fixture.basinW + fixture.gap * (fixture.n - 1);
  const x0 = -totalW / 2;
  const y0 = -fixture.basinD / 2;
  const out: { basin: Vec2[]; faucet: Vec2[] }[] = [];
  for (let i = 0; i < fixture.n; i++) {
    const x = x0 + i * (fixture.basinW + fixture.gap);
    const basin = rect(x, y0, fixture.basinW, fixture.basinD);
    const faucet = rect(
      x + fixture.basinW / 2 - fixture.faucetW / 2,
      y0 + 0.65,
      fixture.faucetW,
      fixture.faucetH
    );
    const place = (poly: Vec2[]) => translate(rotate(poly, pose.angle, 0, 0), pose.x, pose.y);
    out.push({ basin: place(basin), faucet: place(faucet) });
  }
  return out;
}

function maxFillet(points: Vec2[]): number {
  let m = 10;
  for (let i = 1; i < points.length - 1; i++) {
    const a = dist(points[i - 1], points[i]);
    const b = dist(points[i], points[i + 1]);
    m = Math.min(m, a * 0.45, b * 0.45);
  }
  return Math.max(0, m);
}

/** Straight runs joined by clockwise fillets of radius `radius` (0 = sharp corners). */
function compile(points: Vec2[], radius: number): Compiled | null {
  const n = points.length;
  if (n < 2) return null;
  const trims = new Array<number>(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    const vIn = sub(points[i], points[i - 1]);
    const vOut = sub(points[i + 1], points[i]);
    const lenIn = Math.hypot(vIn.x, vIn.y);
    const lenOut = Math.hypot(vOut.x, vOut.y);
    if (lenIn < 1 || lenOut < 1) continue;
    // Positive cross is a right turn in screen space (y down) — the wall-on-the-left turn.
    const cross = vIn.x * vOut.y - vIn.y * vOut.x;
    if (cross <= 0) continue;
    trims[i] = Math.min(radius, lenIn * 0.49, lenOut * 0.49);
  }
  for (let i = 0; i < n - 1; i++) {
    const len = dist(points[i], points[i + 1]);
    const need = trims[i] + trims[i + 1];
    if (need > len * 0.98 && need > 0) {
      const scale = (len * 0.98) / need;
      trims[i] *= scale;
      trims[i + 1] *= scale;
    }
  }

  const segs: Seg[] = [];
  const cornerAt: number[] = [];
  let acc = 0;
  for (let i = 0; i < n - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const len = dist(a, b);
    if (len < 1e-6) continue;
    const dir = { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
    const heading = (Math.atan2(dir.y, dir.x) * 180) / Math.PI;
    const trimA = trims[i];
    const trimB = trims[i + 1];
    const start = { x: a.x + dir.x * trimA, y: a.y + dir.y * trimA };
    const end = { x: b.x - dir.x * trimB, y: b.y - dir.y * trimB };
    const segLen = dist(start, end);
    if (segLen > 0.05) {
      segs.push({ kind: "line", a: start, b: end, heading, length: segLen });
      acc += segLen;
    }
    if (trimB > 0.05 && i + 1 < n - 1) {
      const next = points[i + 2];
      const len2 = dist(b, next);
      if (len2 < 1e-6) continue;
      const dir2 = { x: (next.x - b.x) / len2, y: (next.y - b.y) / len2 };
      const h1raw = (Math.atan2(dir2.y, dir2.x) * 180) / Math.PI;
      let dh = h1raw - heading;
      while (dh <= -180) dh += 360;
      while (dh > 180) dh -= 360;
      const right = { x: -dir.y, y: dir.x };
      const center = { x: end.x + right.x * trimB, y: end.y + right.y * trimB };
      const arcLen = trimB * ((Math.abs(dh) * Math.PI) / 180);
      segs.push({
        kind: "arc",
        center,
        radius: trimB,
        h0: heading,
        h1: heading + dh,
        length: arcLen,
      });
      cornerAt.push(acc + arcLen / 2);
      acc += arcLen;
    }
  }
  if (segs.length === 0 || acc <= 0) return null;
  return { segs, length: acc, cornerAt };
}

function projectLine(seg: LineSeg, q: Vec2): { t: number; dist: number } {
  const dx = seg.b.x - seg.a.x;
  const dy = seg.b.y - seg.a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 < 1e-9 ? 0 : clampNum(((q.x - seg.a.x) * dx + (q.y - seg.a.y) * dy) / len2, 0, 1);
  const x = seg.a.x + dx * t;
  const y = seg.a.y + dy * t;
  return { t, dist: Math.hypot(q.x - x, q.y - y) };
}

function projectArc(seg: ArcSeg, q: Vec2): { t: number; dist: number } {
  const dx = q.x - seg.center.x;
  const dy = q.y - seg.center.y;
  if (dx * dx + dy * dy < 1e-8) {
    const p = arcPoint(seg.center, seg.radius, seg.h0);
    return { t: 0, dist: Math.hypot(q.x - p.x, q.y - p.y) };
  }
  // P(h) = center + R (sin h, −cos h), so h = atan2(sin, cos) = atan2(dx, −dy).
  let h = (Math.atan2(dx, -dy) * 180) / Math.PI;
  while (h < seg.h0 - 1) h += 360;
  while (h > seg.h1 + 1 && h - 360 >= seg.h0 - 1) h -= 360;
  const hc = clampNum(h, seg.h0, seg.h1);
  const p = arcPoint(seg.center, seg.radius, hc);
  const span = seg.h1 - seg.h0;
  const t = Math.abs(span) < 1e-9 ? 0 : (hc - seg.h0) / span;
  return { t, dist: Math.hypot(q.x - p.x, q.y - p.y) };
}

function arcPoint(center: Vec2, radius: number, headingDeg: number): Vec2 {
  const rad = (headingDeg * Math.PI) / 180;
  return {
    x: center.x + radius * Math.sin(rad),
    y: center.y - radius * Math.cos(rad),
  };
}

/** `inner` is inside `outer`: every vertex is inside and no edge crosses the boundary. */
function polygonInside(outer: Vec2[], inner: Vec2[]): boolean {
  for (const p of inner) {
    if (!pointInPolygon(p, outer)) return false;
  }
  for (let i = 0; i < inner.length; i++) {
    const a = inner[i];
    const b = inner[(i + 1) % inner.length];
    for (let j = 0; j < outer.length; j++) {
      const c = outer[j];
      const d = outer[(j + 1) % outer.length];
      if (segmentsProperlyIntersect(a, b, c, d)) return false;
    }
  }
  return true;
}

function segmentsProperlyIntersect(p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2): boolean {
  function ccw(a: Vec2, b: Vec2, c: Vec2): number {
    return (c.y - a.y) * (b.x - a.x) - (b.y - a.y) * (c.x - a.x);
  }
  const d1 = ccw(p3, p4, p1);
  const d2 = ccw(p3, p4, p2);
  const d3 = ccw(p1, p2, p3);
  const d4 = ccw(p1, p2, p4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

function sub(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

function dist(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function clampNum(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
